import type {
  PositionGrade,
  VisitedEvents,
  SDKPlayerHalftimeAvgReceivingMetric,
  SDKPlayerHalftimeAvgRushingMetric,
  FootballGradeFacet,
  SDKEventNarrative,
  SDKSeasonPlayerNarrative,
  SDKPlayerRushingMetric,
  SDKPlayerReceivingMetric,
  PFFLiveData,
  NFLWeekAbbreviation,
  GameScript,
} from '@pff-consumer/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'

import { createDeepEqualSelector } from '../createDeepEqualSelector'

export const CONNECT_KEY = 'connect'

export type PlayerHalftimeAvgReceivingMetric = Record<
  SDKPlayerHalftimeAvgReceivingMetric['playerId'],
  SDKPlayerHalftimeAvgReceivingMetric
>

export type PlayerHalftimeAvgRushingMetric = Record<
  SDKPlayerHalftimeAvgRushingMetric['playerId'],
  SDKPlayerHalftimeAvgRushingMetric
>
export interface ConnectState {
  eventScores: {
    [key: number]: {
      eventId: number
      homeScore?: number
      awayScore?: number
    }
  }
  selectedWeek?: NFLWeekAbbreviation
  selectedSeason?: number
  selectedEvent?: number
  selectedPlayerPosition?: string
  selectedPlayer?: number
  recentPlayerSearch?: number[]
  selectedTeamAbv: string | null
  rushingPlayers: SDKPlayerRushingMetric[]
  receivingPlayers: SDKPlayerReceivingMetric[]
  visitedEventsTimestamp: VisitedEvents
  playerHalftimeReceivingMetricsMap: PlayerHalftimeAvgReceivingMetric
  playerHalftimeRushingMetricsMap: PlayerHalftimeAvgRushingMetric
  playerPositionGradeMap?: Record<number, Record<FootballGradeFacet, PositionGrade[]>>
  teamInsights: {
    narratives: {
      preGame: { [key: number]: SDKSeasonPlayerNarrative[] }
      live: {
        passRate: { [key: number]: SDKEventNarrative }
        pressureRate: { [key: number]: SDKEventNarrative }
      }
    }
  }
  keyInsights: GameScript[]
}

export const initialConnectState: ConnectState = {
  eventScores: {},
  selectedWeek: undefined,
  selectedSeason: undefined,
  selectedEvent: undefined,
  selectedPlayerPosition: undefined,
  selectedPlayer: undefined,
  recentPlayerSearch: undefined,
  visitedEventsTimestamp: {},
  rushingPlayers: [],
  receivingPlayers: [],
  selectedTeamAbv: null,
  playerHalftimeReceivingMetricsMap: {},
  playerHalftimeRushingMetricsMap: {},
  teamInsights: {
    narratives: {
      preGame: {},
      live: {
        pressureRate: {},
        passRate: {},
      },
    },
  },
  keyInsights: [],
}

export const connectSlice = createSlice({
  name: CONNECT_KEY,
  initialState: initialConnectState,
  reducers: {
    setSelectedWeek: (state, { payload }: { payload: NFLWeekAbbreviation }) => {
      state.selectedWeek = payload
    },
    setSelectedSeason: (state, { payload }: { payload: number }) => {
      state.selectedSeason = payload
    },
    setSelectedEvent: (state, { payload }: { payload: number | undefined }) => {
      state.selectedEvent = payload
    },
    setSelectedPlayerPosition: (state, { payload }: { payload: string }) => {
      state.selectedPlayerPosition = payload
    },
    setSelectedPlayer: (state, { payload }: { payload: number }) => {
      state.selectedPlayer = payload
    },
    setRecentPlayerSearch: (state, { payload }: { payload: number[] }) => {
      state.recentPlayerSearch = payload
    },
    setVisitedEventsTimestamp: (state, { payload }: { payload: VisitedEvents }) => {
      state.visitedEventsTimestamp = { ...state.visitedEventsTimestamp, ...payload }
    },
    setPlayerHalftimeReceivingMetricsMap: (state, { payload }) => {
      const playerAvgMetric: PlayerHalftimeAvgReceivingMetric = {}
      payload.forEach((player: SDKPlayerHalftimeAvgReceivingMetric) => {
        playerAvgMetric[player.playerId] = { ...player }
      })
      state.playerHalftimeReceivingMetricsMap = playerAvgMetric
    },
    setPlayerHalftimeRushingMetricsMap: (state, { payload }) => {
      const playerAvgMetric: PlayerHalftimeAvgRushingMetric = {}
      payload.forEach((player: SDKPlayerHalftimeAvgRushingMetric) => {
        playerAvgMetric[player.playerId] = { ...player }
      })
      state.playerHalftimeRushingMetricsMap = playerAvgMetric
    },
    setSelectedTeamAbv: (state, { payload }: { payload: string | null }) => {
      if (state.selectedTeamAbv === payload) {
        state.selectedTeamAbv = null
      } else {
        state.selectedTeamAbv = payload
      }
    },
    processPFFLivePlayers: (state, { payload }: { payload: PFFLiveData }) => {
      const { selectedTeamAbv } = state

      if (!selectedTeamAbv) {
        state.rushingPlayers = payload?.rushingEventData?.players || []
        state.receivingPlayers = payload?.receivingEventData?.players || []
      } else {
        state.rushingPlayers =
          payload?.rushingEventData?.players?.filter((player) => player.teamAbbreviation === selectedTeamAbv) || []
        state.receivingPlayers =
          payload?.receivingEventData?.players?.filter((player) => player.teamAbbreviation === selectedTeamAbv) || []
      }
    },
    setEventScores: (
      state,
      { payload }: { payload: { eventId: number; homeScore?: number; awayScore?: number }[] }
    ) => {
      payload.forEach(({ eventId, homeScore, awayScore }) => {
        state.eventScores[eventId] = { eventId, homeScore, awayScore }
      })
    },
    setPlayerPositionGradeMap: (
      state,
      { payload }: { payload: Record<number, Record<FootballGradeFacet, PositionGrade[]>> }
    ) => {
      state.playerPositionGradeMap = payload
    },
    setPreGameNarratives: (state, { payload }) => {
      payload.forEach((playerNarrative: SDKSeasonPlayerNarrative) => {
        const key = playerNarrative.eventId
        if (!state.teamInsights.narratives.preGame[key]) {
          state.teamInsights.narratives.preGame[key] = []
        }

        if (playerNarrative.narrative) {
          state.teamInsights.narratives.preGame[key].push(playerNarrative)
        }
      })
    },
    setPassRateNarratives: (state, { payload }) => {
      const transformedObject: { [key: number]: SDKEventNarrative } = {}
      payload.forEach((event: SDKEventNarrative) => {
        transformedObject[event.eventId] = event
      })

      state.teamInsights.narratives.live.passRate = transformedObject
    },
    setPressureRateNarratives: (state, { payload }) => {
      const transformedObject: { [key: number]: SDKEventNarrative } = {}
      payload.forEach((event: SDKEventNarrative) => {
        transformedObject[event.eventId] = event
      })

      state.teamInsights.narratives.live.pressureRate = transformedObject
    },
  },
})

export const connectReducer = connectSlice.reducer
export const connectActions = connectSlice.actions

export const getConnectState = (rootState: { [x: string]: ConnectState }): ConnectState => {
  return rootState[CONNECT_KEY]
}

export const connectSelectors = {
  getEventScores: createDeepEqualSelector(getConnectState, (state: ConnectState) => Object.values(state.eventScores)),
  getEventScoreById: (eventId: number) =>
    createSelector(
      getConnectState,
      (state: ConnectState) => state.eventScores[eventId] ?? { homeScore: 0, awayScore: 0, eventId }
    ),
  getSelectedWeek: createSelector(getConnectState, (state: ConnectState) => state.selectedWeek),
  getSelectedSeason: createSelector(getConnectState, (state: ConnectState) => state.selectedSeason),
  getSelectedEvent: createSelector(getConnectState, (state: ConnectState) => state.selectedEvent),
  getSelectedPlayerPosition: createSelector(getConnectState, (state: ConnectState) => state.selectedPlayerPosition),
  getSelectedPlayer: createSelector(getConnectState, (state: ConnectState) => state.selectedPlayer),
  getSelectedTeamAbv: createSelector(getConnectState, (state: ConnectState) => state.selectedTeamAbv),
  getRecentPlayerSearch: createSelector(getConnectState, (state: ConnectState) => state.recentPlayerSearch),
  getVisitedEventsTimestamp: createSelector(getConnectState, (state: ConnectState) => state.visitedEventsTimestamp),
  getPlayerHalftimeReceivingMetric: (playerId: number) =>
    createSelector(getConnectState, (state: ConnectState) => state.playerHalftimeReceivingMetricsMap[playerId]),
  getPlayerHalftimeRushingMetric: (playerId: number) =>
    createSelector(getConnectState, (state: ConnectState) => state.playerHalftimeRushingMetricsMap[playerId]),
  getPFFLivePlayers: createSelector(getConnectState, (state: ConnectState) => ({
    rushingPlayers: state.rushingPlayers,
    receivingPlayers: state.receivingPlayers,
  })),
  getPlayerGradeById: (playerId: number, facet: FootballGradeFacet) =>
    createSelector(getConnectState, (state: ConnectState) => {
      if (!state.playerPositionGradeMap?.[playerId]) return undefined
      const playerGradesByFacets: Record<FootballGradeFacet, PositionGrade[]> = state.playerPositionGradeMap?.[playerId]
      // it is safe to assume a player will only have 1 grade per facet
      const playerGrade = playerGradesByFacets?.[facet]?.[0]
      return playerGrade
    }),
  getLivePassRateGameNarratives: (eventId: number) =>
    createSelector(getConnectState, (state: ConnectState) => state.teamInsights.narratives.live.passRate[eventId]),
  getLivePressureRateGameNarratives: (eventId: number) =>
    createSelector(getConnectState, (state: ConnectState) => state.teamInsights.narratives.live.pressureRate[eventId]),
  getPreGameNarratives: (eventId: number) =>
    createSelector(getConnectState, (state: ConnectState) => state.teamInsights.narratives.preGame[eventId] || []),
  getGameScripts: () => createSelector(getConnectState, (state: ConnectState) => state.keyInsights || []),
}
